import { Type, type Static } from "typebox";
import { OrganizationRoleSchema, PlatformIdentityV1Schema } from "./identity.ts";

export const OrganizationStatusSchema = Type.Union([
  Type.Literal("provisioning"),
  Type.Literal("active"),
  Type.Literal("suspended"),
]);
export type OrganizationStatus = Static<typeof OrganizationStatusSchema>;

export const OwnershipHealthSchema = Type.Union([
  Type.Literal("healthy"),
  Type.Literal("missing"),
  Type.Literal("multiple"),
]);
export type OwnershipHealth = Static<typeof OwnershipHealthSchema>;

export const MembershipStatusSchema = Type.Union([
  Type.Literal("pending"),
  Type.Literal("active"),
  Type.Literal("inactive"),
]);
export type MembershipStatus = Static<typeof MembershipStatusSchema>;

export const InvitationStateSchema = Type.Union([
  Type.Literal("pending"),
  Type.Literal("accepted"),
  Type.Literal("revoked"),
  Type.Literal("expired"),
]);
export type InvitationState = Static<typeof InvitationStateSchema>;

const InternalIdSchema = Type.String({ format: "uuid" });
const WorkOSIdSchema = Type.String({ minLength: 1, maxLength: 255 });
const NameSchema = Type.String({ minLength: 1, maxLength: 200 });
const EmailSchema = Type.String({ format: "email", maxLength: 320 });
const NullableEmailSchema = Type.Union([EmailSchema, Type.Null()]);

export const OrganizationSummaryV1Schema = Type.Object({
  version: Type.Literal(1),
  id: InternalIdSchema,
  name: NameSchema,
  role: OrganizationRoleSchema,
  status: OrganizationStatusSchema,
  ownershipHealth: OwnershipHealthSchema,
  lastActivityAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
}, { additionalProperties: false });
export type OrganizationSummaryV1 = Static<typeof OrganizationSummaryV1Schema>;

export const MemberSummaryV1Schema = Type.Object({
  version: Type.Literal(1),
  id: InternalIdSchema,
  userId: InternalIdSchema,
  displayName: NameSchema,
  email: NullableEmailSchema,
  role: OrganizationRoleSchema,
  status: MembershipStatusSchema,
}, { additionalProperties: false });
export type MemberSummaryV1 = Static<typeof MemberSummaryV1Schema>;

export const InvitationSummaryV1Schema = Type.Union([
  Type.Object({
    version: Type.Literal(1),
    id: InternalIdSchema,
    email: EmailSchema,
    role: OrganizationRoleSchema,
    state: Type.Literal("pending"),
    expiresAt: Type.String({ format: "date-time" }),
  }, { additionalProperties: false }),
  Type.Object({
    version: Type.Literal(1),
    id: InternalIdSchema,
    email: NullableEmailSchema,
    role: OrganizationRoleSchema,
    state: Type.Union([
      Type.Literal("accepted"),
      Type.Literal("revoked"),
      Type.Literal("expired"),
    ]),
    expiresAt: Type.String({ format: "date-time" }),
  }, { additionalProperties: false }),
]);
export type InvitationSummaryV1 = Static<typeof InvitationSummaryV1Schema>;

export const ActiveOwnerV1Schema = Type.Object({
  version: Type.Literal(1),
  organizationId: InternalIdSchema,
  workosOrganizationId: WorkOSIdSchema,
  workosUserId: WorkOSIdSchema,
  displayName: NameSchema,
  email: NullableEmailSchema,
}, { additionalProperties: false });
export type ActiveOwnerV1 = Static<typeof ActiveOwnerV1Schema>;

export const VerifyMembershipRequestV1Schema = Type.Object({
  version: Type.Literal(1),
  workosUserId: WorkOSIdSchema,
  workosOrganizationId: WorkOSIdSchema,
}, { additionalProperties: false });
export type VerifyMembershipRequestV1 = Static<typeof VerifyMembershipRequestV1Schema>;

export const VerifyMembershipResponseV1Schema = Type.Union([
  Type.Object({
    version: Type.Literal(1),
    active: Type.Literal(true),
    membershipStatus: Type.Literal("active"),
    organizationStatus: Type.Literal("active"),
    organizationId: Type.Optional(InternalIdSchema),
  }, { additionalProperties: false }),
  Type.Object({
    version: Type.Literal(1),
    active: Type.Literal(false),
    membershipStatus: Type.Union([
      Type.Literal("inactive"),
      Type.Literal("missing"),
    ]),
    organizationStatus: OrganizationStatusSchema,
    organizationId: Type.Optional(InternalIdSchema),
  }, { additionalProperties: false }),
  Type.Object({
    version: Type.Literal(1),
    active: Type.Literal(false),
    membershipStatus: Type.Literal("active"),
    organizationStatus: Type.Union([
      Type.Literal("provisioning"),
      Type.Literal("suspended"),
    ]),
    organizationId: Type.Optional(InternalIdSchema),
  }, { additionalProperties: false }),
  // A caller can only ask about a known Platform projection. If the provider
  // has no longer got that organization, do not invent an archived state.
  Type.Object({
    version: Type.Literal(1),
    active: Type.Literal(false),
    membershipStatus: Type.Literal("missing"),
    organizationStatus: Type.Literal("missing"),
  }, { additionalProperties: false }),
]);
export type VerifyMembershipResponseV1 = Static<typeof VerifyMembershipResponseV1Schema>;

export const ProvisionOrganizationRequestV1Schema = Type.Object({
  version: Type.Literal(1),
  name: NameSchema,
  ownerEmail: EmailSchema,
}, { additionalProperties: false });
export type ProvisionOrganizationRequestV1 = Static<typeof ProvisionOrganizationRequestV1Schema>;

export const ProvisionOrganizationResponseV1Schema = Type.Object({
  version: Type.Literal(1),
  organizationId: InternalIdSchema,
  status: Type.Union([Type.Literal("provisioning"), Type.Literal("active")]),
}, { additionalProperties: false });
export type ProvisionOrganizationResponseV1 = Static<typeof ProvisionOrganizationResponseV1Schema>;

export const TransferOwnershipRequestV1Schema = Type.Object({
  version: Type.Literal(1),
  targetMembershipId: InternalIdSchema,
}, { additionalProperties: false });
export type TransferOwnershipRequestV1 = Static<typeof TransferOwnershipRequestV1Schema>;

export const RecoverOwnershipRequestV1Schema = Type.Object({
  version: Type.Literal(1),
  selectedMembershipId: InternalIdSchema,
}, { additionalProperties: false });
export type RecoverOwnershipRequestV1 = Static<typeof RecoverOwnershipRequestV1Schema>;

export const MembershipOperationV1Schema = Type.Union([
  Type.Literal("deactivate"),
  Type.Literal("reactivate"),
  Type.Literal("promote_admin"),
  Type.Literal("demote_member"),
  /** Hard-remove pending/inactive access (not soft-deactivate). */
  Type.Literal("remove"),
]);
export type MembershipOperationV1 = Static<typeof MembershipOperationV1Schema>;

export const OrganizationCapabilityV1Schema = Type.Union([
  Type.Literal("invite_member"),
  Type.Literal("manage_member"),
  Type.Literal("manage_admin"),
  Type.Literal("transfer_ownership"),
  Type.Literal("edit_settings"),
  Type.Literal("view_analytics"),
]);
export type OrganizationCapabilityV1 = Static<typeof OrganizationCapabilityV1Schema>;

const OpaqueCursorSchema = Type.String({ minLength: 1, maxLength: 512 });
const NullableOpaqueCursorSchema = Type.Union([OpaqueCursorSchema, Type.Null()]);
const OrganizationCapabilitiesSchema = Type.Array(OrganizationCapabilityV1Schema, { maxItems: 6 });
const MembershipAllowedOperationsSchema = Type.Array(MembershipOperationV1Schema, { maxItems: 5 });
const InvitationAllowedOperationsSchema = Type.Array(Type.Union([
  Type.Literal("resend"),
  Type.Literal("revoke"),
]), { maxItems: 2 });

export const OrganizationPaginationQueryV1Schema = Type.Object({
  cursor: Type.Optional(OpaqueCursorSchema),
  limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 100, default: 50 })),
}, { additionalProperties: false });
export type OrganizationPaginationQueryV1 = Static<typeof OrganizationPaginationQueryV1Schema>;

export const OrganizationListItemV1Schema = Type.Object({
  version: Type.Literal(1),
  organization: OrganizationSummaryV1Schema,
  capabilities: OrganizationCapabilitiesSchema,
}, { additionalProperties: false });
export type OrganizationListItemV1 = Static<typeof OrganizationListItemV1Schema>;

export const OrganizationListResponseV1Schema = Type.Object({
  version: Type.Literal(1),
  items: Type.Array(OrganizationListItemV1Schema, { maxItems: 100 }),
  nextCursor: NullableOpaqueCursorSchema,
}, { additionalProperties: false });
export type OrganizationListResponseV1 = Static<typeof OrganizationListResponseV1Schema>;

export const RosterEntryV1Schema = Type.Union([
  Type.Object({
    version: Type.Literal(1),
    kind: Type.Literal("membership"),
    member: MemberSummaryV1Schema,
    allowedOperations: MembershipAllowedOperationsSchema,
  }, { additionalProperties: false }),
  Type.Object({
    version: Type.Literal(1),
    kind: Type.Literal("invitation"),
    invitation: InvitationSummaryV1Schema,
    allowedOperations: InvitationAllowedOperationsSchema,
  }, { additionalProperties: false }),
]);
export type RosterEntryV1 = Static<typeof RosterEntryV1Schema>;

export const OrganizationRosterResponseV1Schema = Type.Object({
  version: Type.Literal(1),
  organizationId: InternalIdSchema,
  ownershipHealth: OwnershipHealthSchema,
  capabilities: OrganizationCapabilitiesSchema,
  entries: Type.Array(RosterEntryV1Schema, { maxItems: 100 }),
  nextCursor: NullableOpaqueCursorSchema,
}, { additionalProperties: false });
export type OrganizationRosterResponseV1 = Static<typeof OrganizationRosterResponseV1Schema>;

// The target organization is the internal UUID path parameter, never a body field.
export const OrganizationSwitchRequestV1Schema = Type.Object({
  version: Type.Literal(1),
}, { additionalProperties: false });
export type OrganizationSwitchRequestV1 = Static<typeof OrganizationSwitchRequestV1Schema>;

const PlatformCsrfTokenSchema = Type.String({
  minLength: 45,
  maxLength: 128,
  pattern: "^[1-9][0-9]*\\.[A-Za-z0-9_-]{43}$",
});

export const OrganizationSwitchResponseV1Schema = Type.Object({
  version: Type.Literal(1),
  identity: PlatformIdentityV1Schema,
  csrfToken: PlatformCsrfTokenSchema,
}, { additionalProperties: false });
export type OrganizationSwitchResponseV1 = Static<typeof OrganizationSwitchResponseV1Schema>;

/** Roles that may be granted at invite time (owner is never invited). */
export const InviteMemberRoleV1Schema = Type.Union([
  Type.Literal("member"),
  Type.Literal("admin"),
]);
export type InviteMemberRoleV1 = Static<typeof InviteMemberRoleV1Schema>;

export const InviteMemberRequestV1Schema = Type.Object({
  version: Type.Literal(1),
  email: EmailSchema,
  /** Defaults to member when omitted (back-compat). Owner cannot be invited. */
  role: Type.Optional(InviteMemberRoleV1Schema),
}, { additionalProperties: false });
export type InviteMemberRequestV1 = Static<typeof InviteMemberRequestV1Schema>;

export const MembershipCommandRequestV1Schema = Type.Object({
  version: Type.Literal(1),
  operation: MembershipOperationV1Schema,
}, { additionalProperties: false });
export type MembershipCommandRequestV1 = Static<typeof MembershipCommandRequestV1Schema>;

export const InvitationCommandResponseV1Schema = Type.Object({
  version: Type.Literal(1),
  invitation: InvitationSummaryV1Schema,
}, { additionalProperties: false });
export type InvitationCommandResponseV1 = Static<typeof InvitationCommandResponseV1Schema>;

export const MembershipCommandResponseV1Schema = Type.Object({
  version: Type.Literal(1),
  member: MemberSummaryV1Schema,
  ownershipHealth: OwnershipHealthSchema,
}, { additionalProperties: false });
export type MembershipCommandResponseV1 = Static<typeof MembershipCommandResponseV1Schema>;

// Ownership commands affect more than one membership, so they never reuse a
// one-member mutation response or expose provider identifiers.
export const OwnershipCommandResponseV1Schema = Type.Object({
  version: Type.Literal(1),
  organizationId: InternalIdSchema,
  selectedMembershipId: InternalIdSchema,
  ownerMembershipId: InternalIdSchema,
  ownershipHealth: OwnershipHealthSchema,
}, { additionalProperties: false });
export type OwnershipCommandResponseV1 = Static<typeof OwnershipCommandResponseV1Schema>;
